import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Car, Trash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { num, fdate } from "@/lib/format";
import { PageHeader, EmptyState, StatCard } from "@/components/common";
import { TextField, SelectField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ESTATUS = [["inactivo", "Inactivo"], ["disponible", "Disponible"], ["en_ruta", "En ruta"], ["en_servicio", "En servicio"]].map(([value, label]) => ({ value, label }));
const EST_CLS = { inactivo: "bg-neutral-200 text-neutral-700", disponible: "bg-emerald-100 text-emerald-800", en_ruta: "bg-sky-100 text-sky-800", en_servicio: "bg-amber-100 text-amber-800" };
const EMPTY = { marca: "", modelo: "", anio: "", placas: "", numero_economico: "", combustible: "gasolina", kilometraje: "", seguro: "", seguro_vigencia: "", verificacion_fecha: "", estatus: "disponible" };

export default function Flotilla() {
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState({});
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const navigate = useNavigate();

  const load = () => { api.get("/flotilla/vehicles").then((r) => setItems(r.data)); api.get("/flotilla/resumen").then((r) => setResumen(r.data)); };
  useEffect(() => { load(); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.marca) return toast.error("Marca obligatoria");
    try { await api.post("/flotilla/vehicles", { ...form, anio: parseInt(form.anio) || 0, kilometraje: parseFloat(form.kilometraje) || 0, seguro_vigencia: form.seguro_vigencia || null, verificacion_fecha: form.verificacion_fecha || null }); toast.success("Vehículo registrado"); setOpen(false); setForm(EMPTY); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setEst = async (id, estatus) => { await api.patch(`/flotilla/vehicles/${id}/estatus`, null, { params: { estatus } }); load(); };
  const del = async (id) => { if (!window.confirm("¿Eliminar vehículo?")) return; await api.delete(`/flotilla/vehicles/${id}`); load(); };

  const term = q.toLowerCase();
  const filtered = items.filter((v) => !term || [v.marca, v.modelo, v.placas].some((f) => String(f || "").toLowerCase().includes(term)));

  return (
    <div data-testid="flotilla-page">
      <PageHeader title="Vehículos / Flotilla" subtitle="Control de la flotilla de la inmobiliaria" actions={<Button data-testid="new-vehicle-btn" className="rounded-sm gap-2" onClick={() => { setForm(EMPTY); setOpen(true); }}><Plus size={16} /> Nuevo vehículo</Button>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total unidades" value={num(resumen.total || 0)} icon={Car} />
        <StatCard label="Disponibles" value={num(resumen.disponibles || 0)} tone="accent" />
        <StatCard label="En ruta" value={num(resumen.en_ruta || 0)} />
        <StatCard label="En servicio" value={num(resumen.en_servicio || 0)} tone="warn" />
      </div>
      <div className="relative w-full sm:w-96 mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="vehicle-search" placeholder="Buscar por marca, modelo o placas…" className="rounded-sm pl-9" />
      </div>
      {filtered.length === 0 ? <EmptyState title="Sin vehículos" hint="Registra las unidades de la flotilla." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((v) => (
            <div key={v.id} data-testid={`vehicle-card-${v.id}`} className="bg-card border border-border rounded-sm p-4">
              <div className="flex items-start justify-between gap-2">
                <div><div className="font-display font-bold">{v.marca} {v.modelo}</div><div className="text-xs text-muted-foreground">{v.anio || "—"} · {v.placas || "s/placas"} · Econ. {v.numero_economico || "—"}</div></div>
                <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-sm ${EST_CLS[v.estatus] || "bg-secondary"}`}>{v.estatus.replace("_", " ")}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted-foreground">Combustible:</span> {v.combustible}</div>
                <div><span className="text-muted-foreground">Km:</span> {num(v.kilometraje)}</div>
                <div><span className="text-muted-foreground">Seguro:</span> {v.seguro || "—"}</div>
                <div><span className="text-muted-foreground">Verif.:</span> {v.verificacion_fecha ? fdate(v.verificacion_fecha) : "—"}</div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <select value={v.estatus} data-testid={`vehicle-estatus-${v.id}`} onChange={(e) => setEst(v.id, e.target.value)} className="rounded-sm border border-input h-8 text-xs px-2 bg-background flex-1">
                  {ESTATUS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <Button size="sm" variant="outline" className="rounded-sm" data-testid={`vehicle-view-${v.id}`} onClick={() => navigate(`/vehiculos/${v.id}`)}>Ver expediente</Button>
                <Button size="icon" variant="ghost" className="rounded-sm text-red-600" onClick={() => del(v.id)}><Trash size={16} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo vehículo</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField label="Marca" value={form.marca} onChange={set("marca")} testId="vehicle-marca" />
            <TextField label="Modelo" value={form.modelo} onChange={set("modelo")} testId="vehicle-modelo" />
            <TextField label="Año" type="number" value={form.anio} onChange={set("anio")} />
            <TextField label="Placas" value={form.placas} onChange={set("placas")} testId="vehicle-placas" />
            <TextField label="Número económico" value={form.numero_economico} onChange={set("numero_economico")} />
            <SelectField label="Combustible" value={form.combustible} onChange={set("combustible")} options={[["gasolina", "Gasolina"], ["diesel", "Diésel"], ["hibrido", "Híbrido"], ["electrico", "Eléctrico"]].map(([value, label]) => ({ value, label }))} />
            <TextField label="Kilometraje" type="number" value={form.kilometraje} onChange={set("kilometraje")} />
            <SelectField label="Estatus" value={form.estatus} onChange={set("estatus")} options={ESTATUS} />
            <TextField label="Seguro" value={form.seguro} onChange={set("seguro")} />
            <TextField label="Vigencia seguro" type="date" value={form.seguro_vigencia} onChange={set("seguro_vigencia")} />
            <TextField label="Fecha verificación" type="date" value={form.verificacion_fecha} onChange={set("verificacion_fecha")} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-vehicle-btn" onClick={save}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
